import { defaultResultGetter } from "components/Shared/FilterEditor/getResults";
import {
  combineGroupLabels,
  FilterDefinition,
  GetResultFunction,
} from "components/Shared/FilterEditor/utils";
import { AssertionError } from "utils/assertions";

export function getPrefixedId(
  nestedKey: string | null | undefined,
  id: string,
): string {
  if (!nestedKey) return id;
  return `${nestedKey}__${id}`;
}

export type CreateNestedFilterDefinitionsOptions = {
  getId?: (filterDefinition: FilterDefinition, defaultId: string) => string;
  getNestedResult?: GetResultFunction;
  groupLabel?: string | string[];
};

export default function createNestedFilterDefinitions(
  nestedKey: string,
  filterDefinitions: FilterDefinition[],
  {
    getId,
    getNestedResult,
    groupLabel,
  }: CreateNestedFilterDefinitionsOptions = {},
): FilterDefinition[] {
  if (nestedKey.match(/[^a-zA-Z_]+/)) {
    throw new AssertionError(
      "nestedKey can contain only letters or underscores",
    );
  }

  return filterDefinitions.map((filterDef) => {
    const {
      id: originalId,
      getResult,
      groupLabel: existingGroupLabel,
      variants,
      ...rest
    } = filterDef;
    const prefixedId = getPrefixedId(nestedKey, originalId);

    const makeGetNestedResult =
      (getResultFn?: GetResultFunction): GetResultFunction =>
      (value, context) => {
        const { filterDefinition, ...restContext } = context;
        const { id, ...restFilterDefinition } = filterDefinition;
        const newFilterDefinition = {
          id: originalId,
          ...restFilterDefinition,
        };
        const newContext = {
          filterDefinition: newFilterDefinition,
          ...restContext,
        };
        const nestedResult = getResultFn
          ? getResultFn(value, newContext)
          : defaultResultGetter(value, newContext);

        if (!nestedResult) return;

        return { [nestedKey]: nestedResult };
      };

    return {
      id: getId ? getId(filterDef, prefixedId) : prefixedId,
      getResult: makeGetNestedResult(getResult),
      groupLabel: combineGroupLabels(groupLabel, existingGroupLabel),
      variants: variants.map((variant) => {
        const { getResult: getVariantResult, ...variantRest } = variant;
        return {
          getResult:
            getNestedResult ||
            makeGetNestedResult(getVariantResult || getResult),
          ...variantRest,
        };
      }),
      ...rest,
    };
  });
}
